import { mainApi } from '../../../../api/mainApi';
import { middlewareApi } from '../../../middleware/middlewareApi';

import { setAcademicOffers, setOfferStagesConfig, setPeriodList } from './periodsSlice';

import {
  HourhandsByPeriod,
  HourhandsByPeriodResponse,
  Period,
  PeriodByStudentRecord,
  PeriodPayload,
  PeriodPayloadUpdate,
  PeriodsResponse,
  PeriodStagePayload,
  Supplies,
} from '../interfaces/periods.interfaces';
import {
  Stage,
  StagePeriodArrayAssignment,
  StagePeriodAssignment,
  StagePeriodAssignmentQualifications,
  StagePeriodAssignmentQuota,
} from '../interfaces/stages.interfaces';
import { TypePeriod } from '../interfaces/type-periods.interfaces';
import {
  AcademicOfferConfig,
  AcademicOfferingAvailable,
} from '../interfaces/academic-offerings.interfaces';
import {
  PeriodStagesResponse,
  PeriodStage,
  StartDateOnAdminSubjectRegister,
} from '../interfaces/periodStages.interfaces';
import { AccessState } from '../../../../types';
import {
  CalificationModel,
  CalificationModelCourses,
  CoursesStage,
  PeriodStageQualificationsPayload,
} from '../interfaces/calification-models.interfaces';
import { PeriodsWithHourhands } from '../interfaces/PeriodsWithHourdHands';

export const periodsTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['Periods', 'PeriodsAvaliables', 'PeriodStages', 'AcademicOfferings'],
});
// Define a service using a base URL and expected endpoints
export const periodsApi = periodsTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all periods
     */
    getPeriods: builder.query<
      PeriodsResponse,
      {
        profileId?: number;
        page: number;
        perPage: number;
        search: string;
        current?: boolean;
        sort?: string;
        type_sort?: string;
        statusId?: number;
        start?: number;
        start_process?: number;
        currentYear?: number;
        typePeriodId?: string;
      }
    >({
      queryFn: async (
        {
          profileId,
          page,
          search,
          perPage,
          current,
          sort,
          type_sort,
          statusId,
          start_process,
          currentYear,
          typePeriodId,
        },
        { dispatch, getState }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<PeriodsResponse>('/periods', {
            params: {
              user_profile_id,
              page,
              size: perPage,
              type_sort: type_sort || 'desc',
              ...(sort && { sort }),
              ...(currentYear && { per_current_year: currentYear }),
              ...(current && { current }),
              ...(start_process && { start_process }),
              ...(statusId && { status_id: statusId }),
              ...(typePeriodId && { type_period_id: typePeriodId }),
              search,
            },
          });

          dispatch(setPeriodList(data?.data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Periods', id } as const)),
              { type: 'Periods', id: 'LIST' },
            ]
          : [{ type: 'Periods', id: 'LIST' }],
    }),

    /**
     * @GET period by id
     */
    getPeriod: builder.query<Period, { profileId?: number; periodId: number }>({
      queryFn: async ({ profileId, periodId }, { dispatch, getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<Period>(`/periods/${periodId}`, {
            params: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'Periods', profileId }],
    }),

    /**
     * @GET hourhands by period id
     */
    getHourhandsByPeriod: builder.query<
      HourhandsByPeriod[],
      { profileId: number; periodId: number; course_filter?: boolean; student_curriculum?: number }
    >({
      queryFn: async ({ profileId, periodId, course_filter, student_curriculum }) => {
        try {
          const { data } = await mainApi.get<HourhandsByPeriodResponse>(
            `/periods/${periodId}/hourhands`,
            {
              params: {
                user_profile_id: profileId,
                ...(course_filter && { course_filter }),
                ...(student_curriculum && { student_curriculum }),
              },
            }
          );

          const dataAll = Array.isArray(data.data) ? data.data : [];

          return { data: dataAll };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'Periods', profileId }],
    }),

    /**
     * @GET hourhands by period id
     */
    getVigentsHourhands: builder.query<
      HourhandsByPeriod[],
      { profileId: number; period_id?: number }
    >({
      queryFn: async ({ profileId, period_id }) => {
        try {
          const { data } = await mainApi.get<HourhandsByPeriodResponse>(
            `/periods/hourhands/vigents`,
            {
              params: {
                user_profile_id: profileId,
                period_id,
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'Periods', profileId }],
    }),

    /**
     * @GET current period
     */
    getCurrentPeriod: builder.query<
      Period[],
      { profileId?: number; offerId: number; current: boolean; isAsignature?: boolean }
    >({
      queryFn: async ({ profileId, offerId, current, isAsignature }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<Period[]>(`/offers/${offerId}/periods`, {
            params: {
              user_profile_id,
              current,
              isAsignature,
              type_sort: 'desc',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @POST create period
     */
    addPeriod: builder.mutation<
      Period,
      { profileId: number; periodPayload: Partial<PeriodPayload> }
    >({
      queryFn: async ({ profileId, periodPayload }) => {
        try {
          const { data } = await mainApi.post<Period>('/periods', {
            user_profile_id: profileId,
            ...periodPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Periods', id: 'LIST' },
        // combo de periodos en distribucion de aulas y cursos (endpoint duplicado en offerAcademicApiSlice)
        { type: 'AcademicOfferings', id: 'LIST' },
      ],
    }),

    /**
     * @PUT update period
     */
    updatePeriod: builder.mutation<
      Period,
      { profileId: number; periodId: number; periodPayload: Partial<PeriodPayloadUpdate> }
    >({
      queryFn: async ({ profileId, periodId, periodPayload }) => {
        try {
          const { data } = await mainApi.put<Period>(`/periods/${periodId}`, {
            user_profile_id: profileId,
            ...periodPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Periods', id: 'LIST' }],
    }),

    /**
     * @DELETE remove period
     */
    deletePeriod: builder.mutation<Period, { profileId: number; periodId: number }>({
      queryFn: async ({ profileId, periodId }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = currentProfile?.profileId;

        try {
          const { data } = await mainApi.delete(`periods/${periodId}`, {
            data: {
              user_profile_id: user_profile_id || profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Periods', id: 'LIST' },
        { type: 'PeriodsAvaliables', id: 'LIST_AVALIABLES' },
      ],
    }),

    /**
     * @GET all periods avalibles
     */
    getAvailiablesPeriods: builder.query<
      Period[],
      {
        profileId?: number;
        per_current_year?: number;
        offer?: number;
        tp_acronym?: string;
        collaborator_id?: number;
        current?: boolean;
      }
    >({
      queryFn: async (
        { profileId, per_current_year, offer, tp_acronym, collaborator_id, current },
        { getState }
      ) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<Period[]>('/periods', {
            params: {
              user_profile_id: profileId || user_profile_id,
              data: 'all',
              per_current_year,
              offer,
              tp_acronym,
              collaborator_id,
              current,
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'PeriodsAvaliables', id: 'LIST_AVALIABLES' }],
    }),

    /**
     * @GET stage list to combo box
     */
    getAvailableStages: builder.query<Stage[], { profileId?: number }>({
      queryFn: async ({ profileId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get('/stages', {
            params: {
              user_profile_id,
              data: 'all',
            },
          });

          if (!Array.isArray(data)) {
            return { error: 'No se ha encontrado etapas' };
          }

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET period types to combo box
     */
    getAvailableTypePeriods: builder.query<TypePeriod[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get('/type-periods', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET academic offerings availables to combo box
     */
    getAcademicOfferingsAvailables: builder.query<
      AcademicOfferingAvailable[],
      { profileId: number; statusId?: number }
    >({
      queryFn: async ({ profileId, statusId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get('/offers', {
            params: {
              user_profile_id: profileId,
              data: 'all',
              ...(statusId && { status_id: statusId }),
            },
          });

          dispatch(setAcademicOffers(data));

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @POST assign stage to an existing period
     */
    assignStageToPeriod: builder.mutation<
      StagePeriodAssignment,
      { profileId: number; stagePeriodAssignmentPayload: Partial<StagePeriodAssignment> }
    >({
      queryFn: async ({ profileId, stagePeriodAssignmentPayload }) => {
        try {
          const { data } = await mainApi.post('/periodstages', {
            user_profile_id: profileId,
            ...stagePeriodAssignmentPayload,
          });

          return data;
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @POST assign stages array to an existing period quota
     */
    assignStagesArrayToPeriodQuota: builder.mutation<
      StagePeriodAssignment,
      { profileId: number; stagePeriodAssignmentPayload: Partial<StagePeriodAssignmentQuota> }
    >({
      queryFn: async ({ profileId, stagePeriodAssignmentPayload }) => {
        try {
          const { data } = await mainApi.post('/periods/stages/quota', {
            user_profile_id: profileId,
            ...stagePeriodAssignmentPayload,
          });

          return data;
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @POST assign stages array to an existing period Qualification
     */
    assignStagesArrayToPeriodQualification: builder.mutation<
      StagePeriodAssignment,
      {
        profileId: number;
        stagePeriodAssignmentPayload: Partial<StagePeriodAssignmentQualifications>;
      }
    >({
      queryFn: async ({ profileId, stagePeriodAssignmentPayload }) => {
        try {
          const { data } = await mainApi.post('/periods/stages/qualifications', {
            user_profile_id: profileId,
            ...stagePeriodAssignmentPayload,
          });

          return data;
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @POST assign stages array to an existing period
     */
    assignStagesArrayToPeriod: builder.mutation<
      StagePeriodAssignment,
      {
        profileId: number;
        stagePeriodAssignmentPayload: Partial<StagePeriodArrayAssignment>;
      }
    >({
      queryFn: async ({ profileId, stagePeriodAssignmentPayload }) => {
        try {
          const { data } = await mainApi.post('/periods/stages', {
            user_profile_id: profileId,
            ...stagePeriodAssignmentPayload,
          });

          return data;
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET obtain stages by Period id
     */
    getStagesPeriod: builder.query<
      PeriodStagesResponse,
      {
        dataAll?: string;
        user_profile_id: number;
        periodId: number;
        page?: number;
        perPage?: number;
        search?: string;
        sort?: string;
        type_sort?: string;
      }
    >({
      queryFn: async (
        { dataAll, periodId, page, perPage, search, user_profile_id, sort, type_sort },
        { dispatch }
      ) => {
        try {
          const { data } = await mainApi.get<PeriodStagesResponse>(`/periods/${periodId}/stages`, {
            params: {
              user_profile_id: user_profile_id,
              page,
              size: perPage,
              type_sort: type_sort || 'desc',
              ...(sort && { sort }),
              data: dataAll ? dataAll : null,
              search,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'PeriodStages', id } as const)),
              { type: 'PeriodStages', id: 'LIST_PERIOD_STAGES' },
            ]
          : [{ type: 'PeriodStages', id: 'LIST_PERIOD_STAGES' }],
    }),

    /**
     * @PUT edit period stage
     */
    periodStageEdit: builder.mutation<
      PeriodStagePayload,
      { periodstage: number; periodStagePayload: Partial<PeriodStagePayload> }
    >({
      queryFn: async ({ periodstage, periodStagePayload }) => {
        try {
          const { data } = await mainApi.put(`/periods-stages/${periodstage}/update`, {
            ...periodStagePayload,
          });
          return data;
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'PeriodStages', id: 'LIST_PERIOD_STAGES' }],
    }),

    /**
     * @GET Obtain detail period state
     */
    periodStageDetail: builder.query<PeriodStage, { userProfileId: number; periodId: number }>({
      queryFn: async ({ userProfileId, periodId }) => {
        try {
          const { data } = await mainApi.get(`/periodstages/${periodId}`, {
            params: {
              user_profile_id: userProfileId,
              id: periodId,
            },
          });
          return data;
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { userProfileId }) => [{ type: 'PeriodStages', userProfileId }],
    }),

    /**
     * @GET Obtain supplies
     */
    getSuppliesByPeriod: builder.query<Supplies[], { profileId: number; periodId: number }>({
      queryFn: async ({ profileId, periodId }) => {
        try {
          const { data } = await mainApi.get(`/periods/${periodId}/supplies`, {
            params: {
              profile_id: profileId,
              id: periodId,
            },
          });
          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @DELETE Delete period stage
     */
    periodStageDelete: builder.mutation<
      PeriodStage,
      { periodStage: number; user_profile_id: number }
    >({
      queryFn: async ({ periodStage, user_profile_id }) => {
        try {
          const { data } = await mainApi.delete(`/periodstages/${periodStage}`, {
            params: {
              user_profile_id: user_profile_id,
            },
          });
          return data;
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'PeriodStages', id: 'LIST_PERIOD_STAGES' }],
    }),

    /**
     * @GET current and future periods by studentRecordId
     */
    periodByStudentRecordId: builder.query<
      PeriodByStudentRecord[],
      { userProfileId: number; studentRecordId: number }
    >({
      queryFn: async ({ userProfileId, studentRecordId }) => {
        try {
          const { data } = await mainApi.get(
            `/periods/current/student-records/${studentRecordId}`,
            {
              params: {
                user_profile_id: userProfileId,
              },
            }
          );
          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),
    /**
     * @GET Stages by offer academic
     */
    getStagesByOfferAcademicConfig: builder.query<
      AcademicOfferConfig[],
      { profileId: number; offerId: number }
    >({
      queryFn: async ({ profileId, offerId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<AcademicOfferConfig[]>(`/offers/${offerId}/stages`, {
            params: {
              user_profile_id: profileId,
            },
          });

          if (Array.isArray(data)) {
            dispatch(setOfferStagesConfig(data));
          }

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    getStartDateOnAdminSubjectRegister: builder.query<
      StartDateOnAdminSubjectRegister,
      { periodId: number; acronym: string }
    >({
      queryFn: async ({ periodId, acronym }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<StartDateOnAdminSubjectRegister>(
            `/periods/${periodId}/stages/${acronym}`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET get period calification models
     */
    getPeriodCalificationModels: builder.query<
      CalificationModel[],
      { periodId: number; profileId?: number }
    >({
      queryFn: async ({ periodId, profileId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<CalificationModel[]>(
            `periods/${periodId}/califications-model`,
            {
              params: {
                user_profile_id,
              },
            }
          );
          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error) => [{ type: 'PeriodStages', id: 'LIST_PERIOD_STAGES' }],
    }),

    /**
     * @GET get period calification models courses
     */
    getPeriodCalificationModelCourses: builder.query<
      CalificationModelCourses,
      { periodId: number; profileId?: number }
    >({
      queryFn: async ({ periodId, profileId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<CalificationModelCourses>(
            `/periods/${periodId}/califications-model-course-period`,
            {
              params: {
                user_profile_id,
              },
            }
          );
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error) => [{ type: 'PeriodStages', id: 'LIST_PERIOD_STAGES' }],
    }),

    /**
     * @GET get course stages
     */
    getCoursesStages: builder.query<
      CoursesStage[],
      {
        profileId?: number;
        periodId: number;
        qualificationModelId?: number;
        courseId?: number;
        collaboratorId?: number;
      }
    >({
      queryFn: async (
        { profileId, periodId, qualificationModelId, courseId, collaboratorId },
        { getState }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<CoursesStage[]>(
            `/periods/${periodId}/califications-model-courses`,
            {
              params: {
                user_profile_id,
                ...(qualificationModelId && { model_qualifiaction_id: qualificationModelId }),
                ...(courseId && { course_id: courseId }),
                ...(collaboratorId && { collaborator_id: collaboratorId }),
              },
            }
          );
          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error) => [{ type: 'PeriodStages', id: 'LIST_PERIOD_STAGES' }],
    }),

    getPeriodWithHourhands: builder.query<
      PeriodsWithHourhands,
      {
        profileId?: number;
        page?: number;
        perPage?: number;
        search?: string;
        sort?: string;
        type_sort?: string;
      }
    >({
      queryFn: async ({ profileId, page, perPage, search, sort, type_sort }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<PeriodsWithHourhands>(`/periods-hourhands`, {
            params: {
              user_profile_id,
              ...(page && { page }),
              ...(perPage && { per_page: perPage }),
              ...(search && { search }),
              ...(sort && { sort }),
              ...(type_sort && { type_sort }),
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @POST edit period stage
     */
    periodStageQualifications: builder.mutation<
      any,
      {
        profileId?: number;
        periodStageQuaPayload: PeriodStageQualificationsPayload;
      }
    >({
      queryFn: async ({ profileId, periodStageQuaPayload }) => {
        try {
          const { data } = await mainApi.post(`/periods/stages/qualifications`, {
            profileId: profileId,
            ...periodStageQuaPayload,
          });

          return data;
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'PeriodStages', id: 'LIST_PERIOD_STAGES' }],
    }),

    //
  }),
  // overrideExisting: true,
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetPeriodsQuery,
  useLazyGetPeriodsQuery,
  useAddPeriodMutation,
  useDeletePeriodMutation,
  useGetPeriodQuery,
  useUpdatePeriodMutation,
  useGetAvailableStagesQuery,
  useAssignStageToPeriodMutation,
  useGetAvailableTypePeriodsQuery,
  useGetAcademicOfferingsAvailablesQuery,
  useGetAvailiablesPeriodsQuery,
  useGetHourhandsByPeriodQuery,
  useGetStagesPeriodQuery,
  useGetStagesByOfferAcademicConfigQuery,
  usePeriodStageEditMutation,
  usePeriodStageDetailQuery,
  usePeriodStageDeleteMutation,
  useGetCurrentPeriodQuery,
  useLazyGetHourhandsByPeriodQuery,
  useAssignStagesArrayToPeriodQuotaMutation,
  useAssignStagesArrayToPeriodQualificationMutation,
  useGetSuppliesByPeriodQuery,
  usePeriodByStudentRecordIdQuery,
  useGetVigentsHourhandsQuery,
  useAssignStagesArrayToPeriodMutation,
  useLazyGetStartDateOnAdminSubjectRegisterQuery,
  useGetPeriodCalificationModelsQuery,
  useGetPeriodCalificationModelCoursesQuery,
  usePeriodStageQualificationsMutation,
  useGetCoursesStagesQuery,
  useLazyGetPeriodWithHourhandsQuery,
} = periodsApi;
